import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { LangText } from '@/components';
import { preFixImgUrl } from '@/utils';
import CountUp from 'react-countup';
import StarsIcon from '@/assets/svg/stars.svg';
import { useState } from 'react';

const STATS = [
  {
    image: preFixImgUrl('8aa32a57f4cf6652a0e5f554b29a91c5.png'),
    value: '100,000+',
    countTo: 100000,
    suffix: '+',
    decimals: 0,
    valueKey: 'home.why.stats.travelers.value',
    labelKey: 'home.why.stats.travelers.label',
  },
  {
    image: preFixImgUrl('7c5fc29352200fcfe377602f344c930e.png'),
    value: '3,000+',
    countTo: 3000,
    suffix: '+',
    decimals: 0,
    valueKey: 'home.why.stats.partners.value',
    labelKey: 'home.why.stats.partners.label',
  },
  {
    image: preFixImgUrl('5ce7cf1d751cbed9afb2887dcc89e7c2.png'),
    value: '4.8',
    countTo: 4.8,
    suffix: '',
    decimals: 1,
    valueKey: 'home.why.stats.rating.value',
    labelKey: 'home.why.stats.rating.label',
  },
] as const;

const REVIEWS = [
  {
    nameKey: 'home.why.reviews.lifeng.name',
    tripKey: 'home.why.reviews.lifeng.trip',
    contentKey: 'home.why.reviews.lifeng.content',
    tagKey: 'home.why.reviews.lifeng.tag',
    avatar: preFixImgUrl('8578f3662ad645bd01b02480ceadd5c7.png'),
  },
  {
    nameKey: 'home.why.reviews.wanglin.name',
    tripKey: 'home.why.reviews.wanglin.trip',
    contentKey: 'home.why.reviews.wanglin.content',
    tagKey: 'home.why.reviews.wanglin.tag',
    avatar: preFixImgUrl('45abe2debb7392e732733cba60dd6ba2.png'),
  },
  {
    nameKey: 'home.why.reviews.chenyuan.name',
    tripKey: 'home.why.reviews.chenyuan.trip',
    contentKey: 'home.why.reviews.chenyuan.content',
    tagKey: 'home.why.reviews.chenyuan.tag',
    avatar: preFixImgUrl('6024ed51d6707d70710415940aed4da8.png'),
  },
  {
    nameKey: 'home.why.reviews.zhaoming.name',
    tripKey: 'home.why.reviews.zhaoming.trip',
    contentKey: 'home.why.reviews.zhaoming.content',
    tagKey: 'home.why.reviews.zhaoming.tag',
    avatar: preFixImgUrl('a90854fd1b8bdc5108f71fd003597889.png'),
  },
  {
    nameKey: 'home.why.reviews.zhangyan.name',
    tripKey: 'home.why.reviews.zhangyan.trip',
    contentKey: 'home.why.reviews.zhangyan.content',
    tagKey: 'home.why.reviews.zhangyan.tag',
    avatar: preFixImgUrl('540b467cbee0ddd50ba886d775c88494.png'),
  },
] as const;

const REVIEW_CARD_WIDTH = 445;
const REVIEW_CARD_GAP = 52;
const REVIEW_STEP = REVIEW_CARD_WIDTH + REVIEW_CARD_GAP;
const REVIEW_REPEAT_COUNT = 5;
const REVIEW_MIDDLE_COPY = Math.floor(REVIEW_REPEAT_COUNT / 2);
const REVIEW_CENTER_INDEX = REVIEW_MIDDLE_COPY * REVIEWS.length;

const REPEATED_REVIEWS = Array.from({ length: REVIEW_REPEAT_COUNT }).flatMap((_, copyIndex) =>
  REVIEWS.map((review, reviewIndex) => ({
    ...review,
    key: `${copyIndex}-${reviewIndex}-${review.nameKey}`,
  })),
);

const getPageIndex = (index: number) => ((index % REVIEWS.length) + REVIEWS.length) % REVIEWS.length;

const SectionTitle = () => {
  return (
    <div className="flex flex-col items-center">
      <LangText lngKey="home.why.title" className="text-[36px] font-bold leading-[50px] text-[#000]" />
      <span className="mt-[16px] h-[4px] w-[108px] rounded-full bg-primary" />
    </div>
  );
};

const StatItem = ({ item }: { item: typeof STATS[number] }) => {
  return (
    <div className="flex flex-col items-center w-[256px]">
      <img src={item.image} alt="" className="h-[230px] w-[256px] object-contain" draggable={false} />
      <LangText lngKey={item.valueKey} className="mt-[35px] text-[54px] font-bold leading-[62px] text-[#FB5C00]">
        <CountUp
          end={item.countTo}
          duration={1.8}
          decimals={item.decimals}
          separator=","
          suffix={item.suffix}
          enableScrollSpy
          scrollSpyOnce
        />
      </LangText>
      <LangText lngKey={item.labelKey} className="mt-[8px] text-[20px] font-bold leading-[28px] text-[#000]" />
    </div>
  );
};

const ReviewCard = ({ review }: { review: typeof REVIEWS[number] }) => {
  return (
    <div className="h-[280px] w-[445px] shrink-0 rounded-[20px] bg-white p-[32px]">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-[14px]">
          <img src={review.avatar} alt="" className="h-[88px] w-[88px] rounded-full object-cover" draggable={false} />
          <div>
            <LangText lngKey={review.nameKey} className="block text-[20px] font-bold leading-[30px] text-[#000]" />
            <LangText lngKey={review.tripKey} className="mt-[6px] block text-[16px] leading-[22px] text-[#979797]" />
          </div>
        </div>
        <img src={StarsIcon} alt="" className="h-[24px] w-auto shrink-0" draggable={false} />
      </div>
      <LangText lngKey={review.contentKey} className="mt-[30px] block text-[16px] leading-[24px] text-[#000]" />
      <LangText lngKey={review.tagKey} className="mt-[20px] inline-flex rounded-full bg-[#FFF4EE] px-[12px] text-[14px] leading-[26px] text-[#FB5C00]" />
    </div>
  );
};

export const WhyChooseBilionPass = () => {
  const [active, setActive] = useState(REVIEW_CENTER_INDEX);
  const [enableTransition, setEnableTransition] = useState(true);
  const activePage = getPageIndex(active);

  const normalizeActive = () => {
    const normalizedActive = REVIEW_CENTER_INDEX + activePage;

    if (active === normalizedActive) {
      return;
    }

    setEnableTransition(false);
    setActive(normalizedActive);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setEnableTransition(true);
      });
    });
  };

  const goPrev = () => {
    setEnableTransition(true);
    setActive((current) => current - 1);
  };

  const goNext = () => {
    setEnableTransition(true);
    setActive((current) => current + 1);
  };

  const goToPage = (index: number) => {
    setEnableTransition(true);
    setActive(REVIEW_CENTER_INDEX + index);
  };

  return (
    <section className="pt-[82px]">
      <SectionTitle />
      <div
        className="mt-[125px] pt-[0px]"
        style={{
          background: 'linear-gradient(0deg, #FAFAFA 1.87%, #EBF9FF 43.37%, #FAFAFA 94.7%)',
        }}
      >
        <div className="mx-auto flex w-[1200px] items-end justify-between">
          {STATS.map((item) => (
            <StatItem key={item.labelKey} item={item} />
          ))}
        </div>

        <div
          className="relative left-1/2 mt-[118px] h-[280px] w-screen -translate-x-1/2 overflow-hidden"
          style={{
            maskImage: 'linear-gradient(90deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.56) 12%, #000 27%, #000 73%, rgba(0, 0, 0, 0.56) 88%, rgba(0, 0, 0, 0.16) 100%)',
            WebkitMaskImage:
              'linear-gradient(90deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.56) 12%, #000 27%, #000 73%, rgba(0, 0, 0, 0.56) 88%, rgba(0, 0, 0, 0.16) 100%)',
          }}
        >
          <div
            className={`absolute left-1/2 top-0 flex gap-[52px] ${enableTransition ? 'transition-transform duration-500 ease-out' : ''}`}
            onTransitionEnd={normalizeActive}
            style={{ transform: `translateX(-${active * REVIEW_STEP + REVIEW_CARD_WIDTH / 2}px)` }}
          >
            {REPEATED_REVIEWS.map((review) => (
              <ReviewCard key={review.key} review={review} />
            ))}
          </div>
        </div>

        <div className="mt-[48px] flex items-center justify-center gap-[50px]">
          <button
            type="button"
            onClick={goPrev}
            className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-primary text-white"
          >
            <LeftOutlined className="text-[16px]" />
          </button>
          <div className="flex items-center gap-[24px]">
            {REVIEWS.map((review, index) => (
              <button
                key={review.nameKey}
                type="button"
                onClick={() => goToPage(index)}
                className={`h-[12px] w-[12px] rounded-full ${activePage === index ? 'bg-primary' : 'bg-[#D9D9D9]'}`}
                aria-label={`${index + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={goNext}
            className="flex h-[40px] w-[40px] items-center justify-center rounded-full bg-primary text-white"
          >
            <RightOutlined className="text-[16px]" />
          </button>
        </div>
      </div>
    </section>
  );
};
